import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography, Box, Button, TextField, MenuItem, IconButton,
  Select, Card, Grid, Tooltip
} from '@mui/material';
import Header from '../../Header/Header';
import Footer2 from '../Footer2';
import Internships from '../../Icon/Internship.png';
import Jobs from '../../Icon/Jobs2.png';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const API_URL = process.env.REACT_APP_API_URL;

function Careers() {
  const [internshipRole, setInternshipRole] = useState('');
  const [jobRole, setJobRole] = useState('');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [resume, setResume] = useState(null);
  const [showForm, setShowForm] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const validateInputs = () => {
    if (!name || !email || !contact || !resume) {
      alert("All fields are required."); return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert("Invalid email."); return false;
    }
    if (!/^\d{10}$/.test(contact)) {
      alert("Contact must be 10 digits."); return false;
    }
    return true;
  };

  const handleApply = async () => {
    if (!validateInputs()) return;
    const type = showForm;
    const role = type === 'internship' ? internshipRole : jobRole;
    const data = new FormData();
    ["name", "contact", "email"].forEach(k => data.append(k, { name, contact, email }[k]));
    data.append('role', role);
    data.append('type', type);
    data.append('is_active', true);
    data.append('resume', resume);

    try {
      await axios.post(`${API_URL}/create_application/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Applied for ${type.toUpperCase()}: ${role}`);
      window.location.reload();
    } catch {
      alert("Submission error.");
    }
  };

  const handleFile = e => {
    const file = e.target.files[0];
    if (file?.size > 1 * 1024 * 1024) {
      alert("Max 1 MB file size."); return;
    }
    setResume(file);
  };

  const removeFile = () => setResume(null);

  const renderForm = () => (
    <Card sx={{
      mt: 3, p: 3, width: '100%', maxWidth: 500,
      bgcolor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(6px)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
    }}>
      <Typography variant="h6" align="center" mb={2} sx={{ color: '#fff' }}>
        {showForm === 'internship' ? 'Internship Application' : 'Job Application'}
      </Typography>

      <Select
        fullWidth value={showForm === 'internship' ? internshipRole : jobRole}
        onChange={e => showForm === 'internship' ? setInternshipRole(e.target.value) : setJobRole(e.target.value)}
        displayEmpty sx={{
          mb: 2, bgcolor: '#222', color: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1E90FF' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00CFFF' },
        }}
      >
        <MenuItem value="" disabled>Select Role</MenuItem>
        {(showForm === 'internship'
          ? ['Software Developer', 'DevOps', 'Data Science', 'Marketing', 'Content Creation']
          : ['Legal & Compliance', 'Finance', 'DevOps', 'Data Science', 'AI Research', 'Digital Marketing']
        ).map(role => <MenuItem key={role} value={role}>{role}</MenuItem>)}
      </Select>

      {['name', 'contact', 'email'].map((field) => (
        <TextField
          key={field}
          label={field.charAt(0).toUpperCase() + field.slice(1)}
          value={{ name, contact, email }[field]}
          onChange={e => ({ name: setName, contact: setContact, email: setEmail }[field](e.target.value))}
          fullWidth variant="outlined" sx={{
            mb: 2, bgcolor: '#222',
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#555' },
              '&:hover fieldset': { borderColor: '#1E90FF' },
              '&.Mui-focused fieldset': { borderColor: '#00CFFF' },
              '& input': { color: '#fff' }
            }
          }}
        />
      ))}

      <Box
        onClick={() => document.getElementById('fileInput').click()}
        sx={{
          border: '2px dashed #777', borderRadius: 2, py: 2, textAlign: 'center',
          bgcolor: resume ? 'rgba(30,144,255,0.1)' : '#111', cursor: 'pointer', mb: 2
        }}
      >
        <input id="fileInput" type="file" accept=".pdf,.doc,.docx" hidden onChange={handleFile} />
        <Typography sx={{ color: '#fff' }}>
          {resume
            ? <>
                {resume.name}
                <Button size="small" onClick={removeFile} sx={{ ml: 1, color: 'red' }}>Remove</Button>
              </>
            : 'Click to upload resume (PDF, DOC, max 1MB)'}
        </Typography>
      </Box>

      <Button
        fullWidth variant="contained"
        sx={{
          py: 1.5,
          bgcolor: 'linear-gradient(to right, #1E90FF, #00CFFF)',
          color: '#fff',
          '&:hover': { bgcolor: 'linear-gradient(to right, #00CFFF, #1E90FF)' }
        }}
        onClick={handleApply}
      >
        Submit
      </Button>
    </Card>
  );

  return (
    <Box sx={{ bgcolor: '#000', minHeight: '100vh', pb: 6 }}>
      <Header />

      <Box sx={{ textAlign: 'center', pt: 8, px: 2 }}>
        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 4 }}>
          Careers
        </Typography>
        <Typography variant="h5" sx={{ color: '#ccc', mb: 6, px: { xs: 1, md: 4 } }}>
          Join our mission—grow with us in AI, innovation, and impact.
        </Typography>

        <Grid container spacing={4} justifyContent="center" sx={{ px: { xs: 1, md: 6 } }}>
          {[
            { title: 'Innovate with AI', desc: 'Solve real-world challenges through cutting-edge technology.' },
            { title: 'Startup Growth', desc: 'Make a meaningful impact in a nimble environment.' },
            { title: 'Career Development', desc: 'Enhance skills through mentorship and hands-on learning.' },
            { title: 'Inclusive Culture', desc: 'Join a collaborative, diverse team.' },
            { title: 'Meaningful Work', desc: 'Contribute to socially impactful projects.' },
            { title: 'Work-Life Balance', desc: 'Enjoy flexibility and growth support.' },
          ].map((item, i) => (
            <Grid key={i} item xs={12} sm={6} md={4}>
              <Box sx={{
                p: 2, bgcolor: 'rgba(10, 10, 10, 1)', borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)', textAlign: 'center',
                transition: 'transform 0.3s', '&:hover': { transform: 'scale(1.03)' }
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: '#ccc', fontSize: '0.9rem' }}>
                  {item.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {[
            { label: 'Internships', img: Internships },
            { label: 'Jobs', img: Jobs }
          ].map((plan) => (
            <Box key={plan.label} sx={{
              width: '90%', maxWidth: 500, p: 2, my: 3,
              bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                {plan.label}
              </Typography>
              <Box component="img" src={plan.img} alt={plan.label} sx={{ width: '100%', mb: 2, borderRadius: 1 }} />
              <Button
                variant="contained"
                sx={{
                  px: 4, py: 1.5,
                  bgcolor: 'linear-gradient(to right, #1E90FF, #00CFFF)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'linear-gradient(to right, #00CFFF, #1E90FF)' }
                }}
                onClick={() => setShowForm(showForm === plan.label.toLowerCase() ? null : plan.label.toLowerCase())}
              >
                Apply
              </Button>
              {showForm === plan.label.toLowerCase() && renderForm()}
            </Box>
          ))}
        </Box>
      </Box>

      <Tooltip title="Scroll to Top">
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed', bottom: 20, right: 20,
            bgcolor: 'rgba(30,144,255,0.9)', color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,206,255,0.9)' }
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
      </Tooltip>

      <Footer2 />
    </Box>
  );
}

export default Careers;
